import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import {
  getUserFileById,
  getUserFileMetadata,
} from "../../../services/fileServices/UserDriverService";
import { UserFile, UserFileMetadata } from "../../../interfaces/userDrive/userFile";
import FileViewer from "react-file-viewer";
import { formatFileSize } from "../../../utilities/fileUtils";
import { formatDateTime } from "../../../utilities/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import UserFilePDFViewer from "../fileViewer/pdfViewer/UserFilePDFViewer";
import SpreadsheetEditor from "../fileViewer/excel/SpreadsheetEditor";
import { getFileDownloadByIdAsync, GetFileMetadataAsync } from "../../../services/fileServices/FileService";
import FilePDFViewer from "./FilePDFViewer";
import backarrowbtn from '../../../assets/images/backarrowbtn.svg'

const ViewFile: React.FC = () => {
  const { currentUser } = useAuth();
  const { fileId, fileTable } = useParams();
  const navigate = useNavigate();

  const [fileUrl, setFileUrl] = useState("");
  const [fileMetadata, setFileMetadata] = useState<UserFileMetadata | null>(null);
  const [blobMimeType, setBlobMimeType] = useState<string>("");
  const [scale, setScale] = useState(1.0);
  const [zoom, setZoom] = useState<number>(1.0);
  const [textContent, setTextContent] = useState<string>("");

  useEffect(() => {
    const fetchFile = async () => {
      if (!currentUser || !fileId || !fileTable) return;
      try {
        const authToken = await currentUser.getIdToken();
        const file = await GetFileMetadataAsync(fileId, fileTable, authToken);
        if (!file) return;

        setFileMetadata(file);
        const fileLoc = await getFileDownloadByIdAsync(fileId, fileTable, authToken);
        if (!fileLoc) return;

        setFileUrl(fileLoc);
        const response = await fetch(fileLoc);
        const blob = await response.blob();

        let inferredMimeType = file.mimeType || blob.type;
        setBlobMimeType(inferredMimeType);

        if (inferredMimeType === "text/plain") {
          const text = await response.text();
          setTextContent(text);
        }
      } catch (error) {
        console.error("Error fetching file:", error);
      }
    };
    fetchFile();
  }, [currentUser, fileId]);

  const downloadFile = () => {
    if (!fileUrl || !fileMetadata?.name) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileMetadata.name; // this suggests a filename to the browser
    link.click();
  }

  const renderViewer = () => {
    if (!fileUrl) return <div>Loading...</div>;

    const extension = fileMetadata?.name?.split(".").pop()?.toLowerCase();

    if (blobMimeType === "application/pdf" || extension === "pdf") {
      return <FilePDFViewer fileUrl={fileUrl} scale={scale} tableType={fileTable || ""} />;
    }

    if (blobMimeType.startsWith("image/")) {
      return (
        <div className="flex flex-col items-center overflow-hidden h-full">
          <img src={fileUrl} alt={fileMetadata?.name} className="max-w-full" />
          <div className="flex items-center gap-2 mt-2">
            <label>Zoom: </label>
            <input
              type="range"
              min="0.5"
              max="3.0"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
            <span>{zoom.toFixed(1)}x</span>
          </div>
        </div>
      );
    }

    if (blobMimeType === "text/plain") {
      return (
        <pre className="p-4 bg-white overflow-auto h-full">{textContent}</pre>
      );
    }

    if (blobMimeType.startsWith("video/")) {
      return <video src={fileUrl} controls className="w-full h-full" />;
    }

    if (blobMimeType.includes("spreadsheet")) {
      return (
        <div className="overflow-auto h-full">
          <SpreadsheetEditor fileUrl={fileUrl} />
        </div>
      );
    }

    return <FileViewer fileType={extension || "unknown"} filePath={fileUrl} />;
  };

  return (
    <>
      <div className="common_card">
        <div className="top top_bar_flx">
          <div className="top_bar_tittle">
            <h2 title={fileMetadata?.name} ><button><img style={{ width: '1.5rem' }} src={backarrowbtn} alt="backarrowbtn" /></button>{fileMetadata?.name}</h2>
          </div>
          {fileUrl && (
            <button
              className="border px-3 py-1 rounded"
              onClick={downloadFile}
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          )}
        </div>
        <div className="sign_body">
          <div className="sign_left">
            <div className="sign_right_body">
              {renderViewer()}
            </div>
          </div>
          <div className="sign_right">
            <div className="common_card h-100">
              <div className="top border_tittle">
                <div className="top_bar_tittle">
                  <h2>Document Info</h2>
                </div>
              </div>
              <div className="document_stats_grid">
                <div className="stats">
                  <h3>File Type</h3>
                  <p>{fileMetadata?.mimeType || "N/A"}</p>
                </div>
                <div className="stats">
                  <h3>Created</h3>
                  <p>
                    {fileMetadata?.createDate
                      ? formatDateTime(
                        new Date(
                          fileMetadata.createDate
                        )
                      )
                      : "N/A"}
                  </p>
                </div>
                <div className="stats">
                  <h3>File Size</h3>
                  <p>
                    {formatFileSize(fileMetadata?.size || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewFile;
